// FILE: src/pages/Employee/EmployeeDashboard.js
//
// FIXES APPLIED (vs previous version):
// ─────────────────────────────────────────────────────────────────
// FIX 1 — iOS autoplay: after assigning stream to videoRef,
//          explicitly call videoRef.current.play().catch(()=>{})
// FIX 2 — Camera race on iOS: stop any existing streamRef before
//          calling getUserMedia (not just on cleanup)
// FIX 3 — facingMode exact with fallback for older iPhones
// FIX 4 — aspectRatio: '4/3' replaced with fixed height (Samsung fix)
// FIX 5 — geolocation timeout bumped to 20000ms for iOS first-use
// ─────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress,
  Button, Divider, List, ListItem, ListItemIcon,
  ListItemText, Stack, TextField, Modal, Alert,
  Avatar, Chip
} from '@mui/material';
import {
  CalendarMonth, EventNote, PlayArrow, Stop,
  AccessTime, CameraAlt, MyLocation, CheckCircle,
  PhotoCamera, LocationOn
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/* ─── helpers ─── */
const getTodayDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now - offset).toISOString().split('T')[0];
};

const isLateNow = () => {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  return (h > 9) || (h === 9 && m > 40);
};

/* ─────────────────────────────────────────────────────────────────
   CAMERA MODAL — with all iOS + Android browser fixes
─────────────────────────────────────────────────────────────────── */
const CameraModal = ({ open, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');

    // FIX 2: stop any existing stream before requesting a new one
    // prevents "hardware already in use" on iOS when modal reopens fast
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    // FIX 3: try exact facingMode first, fall back for single-camera devices (some iPads)
    const tryGetCamera = (constraints) =>
      navigator.mediaDevices.getUserMedia(constraints);

    tryGetCamera({
      video: { facingMode: { exact: 'user' }, width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false
    })
      .catch(() =>
        // fallback: drop exact constraint for devices with one camera
        tryGetCamera({ video: { facingMode: 'user' }, audio: false })
      )
      .catch(() =>
        // last resort: any camera
        tryGetCamera({ video: true, audio: false })
      )
      .then(s => {
        streamRef.current = s;
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          // FIX 1: explicitly call play() — iOS Safari won't auto-start
          // even with autoPlay attribute when stream is assigned in useEffect
          videoRef.current.play().catch(() => {});
        }
      })
      .catch(() => {
        setError('Camera access denied. Please allow camera permission and try again.');
      });

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStream(null);
  };

  const handleCapture = () => {
    if (!videoRef.current || !stream) return;
    setCapturing(true);
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      stopStream();
      setCapturing(false);
      onCapture(blob);
    }, 'image/jpeg', 0.92);
  };

  const handleClose = () => { stopStream(); onClose(); };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95vw', sm: 480 },
        bgcolor: '#0f172a', borderRadius: 3, p: 2, outline: 'none'
      }}>
        <Typography variant="h6" color="white" fontWeight={700} mb={1.5} textAlign="center">
          <CameraAlt sx={{ mr: 1, verticalAlign: 'middle' }} />
          Capture Selfie
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Box sx={{
            position: 'relative', borderRadius: 2, overflow: 'hidden',
            bgcolor: '#1e293b', mb: 2,
            // FIX 4: fixed height instead of aspectRatio CSS (Samsung Internet < v16 fix)
            height: { xs: 240, sm: 320 }
          }}>
            <video
              ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
            />
            {!stream && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={40} sx={{ color: 'white' }} />
              </Box>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <Button fullWidth variant="outlined"
            sx={{ color: 'white', borderColor: '#334155', borderRadius: 2 }}
            onClick={handleClose}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" color="success"
            startIcon={capturing ? <CircularProgress size={18} color="inherit" /> : <PhotoCamera />}
            disabled={!stream || !!error || capturing}
            onClick={handleCapture}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            {capturing ? 'Capturing...' : 'Take Photo'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
─────────────────────────────────────────────────────────────────── */
const EmployeeDashboard = () => {

  const { user } = useAuth();
  const employeeId = user?.employeeId;

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [workingSeconds, setWorkingSeconds] = useState(0);
  const [lateReason, setLateReason] = useState('');
  const [dashboardData, setDashboardData] = useState({
    attendanceStatus: 'Not Clocked In',
    pendingLeaves: 0,
    branchName: 'N/A'
  });

  const [cameraOpen, setCameraOpen] = useState(false);
  const [clockAction, setClockAction] = useState('in');
  const [gpsLocation, setGpsLocation] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState('');
  const [locationError, setLocationError] = useState('');
  const [clockError, setClockError] = useState('');

  useEffect(() => {
    return () => { if (selfiePreview) URL.revokeObjectURL(selfiePreview); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const fetchDashboardData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const todayStr = getTodayDate();
      const attendanceRes = await api.get(`/attendance/employee/${employeeId}`);
      const records = attendanceRes.data || [];
      const todayRecords = records.filter(r => r.date === todayStr);

      let latest = null;
      if (todayRecords.length > 0) {
        latest = todayRecords.sort((a, b) => b.id - a.id)[0];
      }

      const hasClockIn = latest?.clockInTime !== null && latest?.clockInTime !== '';
      const hasClockOut = latest?.clockOutTime !== null &&
        latest?.clockOutTime !== '' && latest?.clockOutTime !== '00:00:00';

      if (hasClockIn && !hasClockOut) {
        setTodayRecord(latest);
      } else {
        setTodayRecord(null);
      }

      if (hasClockIn && !hasClockOut) {
        const now = new Date();
        const [h, m, s] = latest.clockInTime.split(':');
        const clockIn = new Date();
        clockIn.setHours(h, m, s);
        const diff = Math.floor((now - clockIn) / 1000);
        setWorkingSeconds(diff > 0 ? diff : 0);
      } else {
        setWorkingSeconds(0);
      }

      const leaveRes = await api.get(`/leaves/emp/${employeeId}`);
      const pendingLeaves = leaveRes.data.filter(l => l.status?.startsWith('PENDING')).length;

      setDashboardData({
        attendanceStatus: (hasClockIn && !hasClockOut) ? 'Currently Clocked In' : 'Not Clocked In',
        pendingLeaves,
        branchName: user?.branchName || 'Main Office'
      });

    } catch (err) {
      console.error(err);
      toast.error('Dashboard load failed');
    } finally {
      setLoading(false);
    }
  }, [employeeId, user?.branchName]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  useEffect(() => {
    if (!todayRecord) return;
    const interval = setInterval(() => setWorkingSeconds(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [todayRecord]);

  /* ── STEP 1: validate → get GPS → open camera ── */
  const startClockAction = (action) => {
    setClockError('');
    setLocationError('');

    if (action === 'in' && isLateNow() && (!lateReason || lateReason.trim() === '')) {
      setClockError('Please enter a reason for late login before clocking in.');
      return;
    }

    setClockAction(action);

    if (!navigator.geolocation) {
      setCameraOpen(true);
      return;
    }

    setActionLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setActionLoading(false);
        setCameraOpen(true);
      },
      () => {
        setActionLoading(false);
        setLocationError('Location access denied. Clock-in will be blocked if your branch requires geofencing.');
        setGpsLocation(null);
        setCameraOpen(true);
      },
      {
        enableHighAccuracy: true,
        // FIX 5: 20s timeout — iOS shows permission dialog before GPS starts,
        // 10s can expire while user is still reading the dialog on first use
        timeout: 20000,
        maximumAge: 0
      }
    );
  };

  /* ── STEP 2: selfie captured → submit ── */
  const handleSelfieCapture = async (blob) => {
    setCameraOpen(false);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview(URL.createObjectURL(blob));
    await submitClockAction(blob);
  };

  /* ── STEP 3: build FormData and POST/PUT ── */
  const submitClockAction = async (selfieBlob) => {
    setActionLoading(true);
    setClockError('');
    try {
      const formData = new FormData();
      formData.append('selfie', selfieBlob, 'selfie.jpg');
      if (gpsLocation) {
        formData.append('latitude', String(gpsLocation.latitude));
        formData.append('longitude', String(gpsLocation.longitude));
      }
      if (clockAction === 'in' && isLateNow() && lateReason) {
        formData.append('lateReason', lateReason);
      }

      if (clockAction === 'in') {
        await api.post('/attendance/clock-in', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setLateReason('');
        toast.success('✅ Clocked In');
      } else {
        await api.put('/attendance/clock-out', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('✅ Clocked Out');
      }

      await fetchDashboardData();

    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || '';
      const friendly = msg || (clockAction === 'in' ? 'Clock-in failed.' : 'Clock-out failed.');
      setClockError(friendly);
      toast.error(friendly);
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfiePreview('');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const googleMapsUrl = gpsLocation
    ? `https://www.google.com/maps?q=${gpsLocation.latitude},${gpsLocation.longitude}`
    : null;

  return (
    <Box sx={{ p: 4 }}>

      <Typography variant="h4" fontWeight={900} gutterBottom>
        My Workspace
      </Typography>

      <Typography sx={{ mb: 4, color: 'text.secondary' }}>
        Welcome back, <strong>{user?.username}</strong> | {dashboardData.branchName}
      </Typography>

      <Grid container spacing={4}>

        {/* ── LEFT PANEL ── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 5 }}>

            <Typography variant="h6" fontWeight={700}>Today Overview</Typography>
            <Divider sx={{ my: 2 }} />

            <Typography variant="h5" fontWeight={800}>
              Status:{' '}
              <Box component="span" sx={{ color: todayRecord ? 'green' : 'red' }}>
                {dashboardData.attendanceStatus}
              </Box>
            </Typography>

            {todayRecord && (
              <Typography sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <AccessTime />
                Working: <b>{formatDuration(workingSeconds)}</b>
              </Typography>
            )}

            {todayRecord?.clockInLatitude && todayRecord?.clockInLongitude && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                <LocationOn sx={{ fontSize: 15, color: '#0284c7' }} />
                <Typography
                  fontSize={12}
                  component="a"
                  href={`https://www.google.com/maps?q=${todayRecord.clockInLatitude},${todayRecord.clockInLongitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#0284c7', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  View clock-in location on map
                  ({parseFloat(todayRecord.clockInLatitude).toFixed(4)}, {parseFloat(todayRecord.clockInLongitude).toFixed(4)})
                </Typography>
              </Stack>
            )}

            <List sx={{ mt: 3 }}>
              <ListItem>
                <ListItemIcon><CalendarMonth /></ListItemIcon>
                <ListItemText primary="Pending Leaves" secondary={`${dashboardData.pendingLeaves} requests`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><EventNote /></ListItemIcon>
                <ListItemText primary="Attendance Matrix" secondary="Monthly overview" />
              </ListItem>
            </List>

            <Button fullWidth variant="outlined" component={Link} to="/employee/attendance" sx={{ mt: 2 }}>
              Open Attendance
            </Button>

          </Paper>
        </Grid>

        {/* ── RIGHT PANEL — clock actions ── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 5 }}>

            <Typography variant="h6" fontWeight={700}>Quick Actions</Typography>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>

              {gpsLocation && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <MyLocation sx={{ fontSize: 15, color: '#16a34a' }} />
                  <Typography
                    fontSize={12}
                    component="a"
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#16a34a', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    📍 Location acquired — tap to verify on map
                    ({gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)})
                  </Typography>
                </Stack>
              )}

              {locationError && (
                <Alert severity="warning" onClose={() => setLocationError('')} sx={{ borderRadius: 2 }}>
                  {locationError}
                </Alert>
              )}
              {clockError && (
                <Alert severity="error" onClose={() => setClockError('')} sx={{ borderRadius: 2 }}>
                  {clockError}
                </Alert>
              )}

              {!todayRecord && isLateNow() && (
                <TextField
                  fullWidth
                  label="Reason for late login *"
                  value={lateReason}
                  onChange={e => setLateReason(e.target.value)}
                  size="small"
                />
              )}

              {selfiePreview && (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar src={selfiePreview} sx={{ width: 56, height: 56, border: '2px solid #16a34a' }} />
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CheckCircle sx={{ fontSize: 15, color: '#16a34a' }} />
                      <Typography fontSize={13} color="#16a34a" fontWeight={600}>Selfie captured</Typography>
                    </Stack>
                    <Typography fontSize={11} color="text.secondary">Visible to your Approval Manager</Typography>
                  </Box>
                </Stack>
              )}

              <Button
                variant="contained"
                color={todayRecord ? 'error' : 'success'}
                startIcon={
                  actionLoading
                    ? <CircularProgress size={20} color="inherit" />
                    : todayRecord ? <Stop /> : <PlayArrow />
                }
                onClick={() => startClockAction(todayRecord ? 'out' : 'in')}
                disabled={actionLoading}
                sx={{ py: 2, fontWeight: 900 }}
              >
                {todayRecord ? 'CLOCK OUT' : 'CLOCK IN'}
              </Button>

              <Typography fontSize={11} color="text.secondary" textAlign="center">
                📍 GPS + selfie required. Fake GPS apps are detected server-side via accuracy
                &amp; speed checks — tampering results in rejected attendance.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Button fullWidth component={Link} to="/employee/calendar">Calendar</Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth component={Link} to="/employee/daily-pay">My Pay</Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth component={Link} to="/employee/missed-punch" color="warning">
                    Missed Punch
                  </Button>
                </Grid>
              </Grid>

            </Stack>
          </Paper>
        </Grid>

      </Grid>

      <CameraModal
        open={cameraOpen}
        onCapture={handleSelfieCapture}
        onClose={() => setCameraOpen(false)}
      />

    </Box>
  );
};

export default EmployeeDashboard;